/**
 * A pure-SVG 30-day sparkline. No external chart lib — we render as compactly
 * as possible so it drops into any account card without weight.
 *
 * Props:
 *   series: [{d: "2026-02-01", v: 12345.67}, ...]  — chronological
 *   width, height, stroke: visual tuning
 *   accent: "emerald" | "rose" | "stone" — colour family (auto if omitted)
 *   showLast: render a dot at the tip (default true)
 */
import { useMemo } from "react";

const ACCENTS = {
  emerald: { line: "#059669", fill: "rgba(5,150,105,0.18)", dot: "#059669" },
  rose:    { line: "#e11d48", fill: "rgba(225,29,72,0.18)", dot: "#e11d48" },
  stone:   { line: "#78716c", fill: "rgba(120,113,108,0.18)", dot: "#78716c" },
  gold:    { line: "#b8935a", fill: "rgba(184,147,90,0.20)", dot: "#b8935a" },
};

export default function Sparkline({
  series,
  width = 140,
  height = 36,
  stroke = 1.5,
  accent,
  showLast = true,
  className = "",
}) {
  const { d, area, dot, colour } = useMemo(() => {
    if (!series || series.length < 2) return { d: "", area: "", dot: null, colour: ACCENTS.stone };
    const values = series.map((p) => p.v);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const stepX = width / (series.length - 1);
    const pad = 2;
    const y = (v) => pad + (height - pad * 2) * (1 - (v - min) / span);
    const points = series.map((p, i) => [i * stepX, y(p.v)]);
    const linePath = points
      .map(([x, py], i) => `${i === 0 ? "M" : "L"}${x.toFixed(2)},${py.toFixed(2)}`)
      .join(" ");
    // Close down to baseline for a soft area fill.
    const [x0] = points[0];
    const [xN] = points[points.length - 1];
    const areaPath = `${linePath} L${xN.toFixed(2)},${height} L${x0.toFixed(2)},${height} Z`;
    // Colour heuristic: last value sign
    const last = values[values.length - 1];
    const first = values[0];
    const chosen =
      accent && ACCENTS[accent]
        ? ACCENTS[accent]
        : last > first
          ? ACCENTS.emerald
          : last < first
            ? ACCENTS.rose
            : ACCENTS.stone;
    const lastPt = points[points.length - 1];
    return {
      d: linePath,
      area: areaPath,
      dot: showLast ? { x: lastPt[0], y: lastPt[1] } : null,
      colour: chosen,
    };
  }, [series, width, height, accent, showLast]);

  if (!series || series.length < 2) {
    return (
      <svg
        width={width}
        height={height}
        className={className}
        role="img"
        aria-label="No trend data"
      >
        <line
          x1="0"
          y1={height / 2}
          x2={width}
          y2={height / 2}
          stroke="#d6d3d1"
          strokeDasharray="2 3"
          strokeWidth="1"
        />
      </svg>
    );
  }

  return (
    <svg width={width} height={height} className={className} role="img" aria-label="30-day trend">
      <path d={area} fill={colour.fill} stroke="none" />
      <path d={d} fill="none" stroke={colour.line} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" />
      {dot && <circle cx={dot.x} cy={dot.y} r={2.4} fill={colour.dot} />}
    </svg>
  );
}
